import { SsoEmailDomain } from '@choco-admin/types';
import { Button, Chip, Switch } from '@heroui/react';
import React, { useState } from 'react';

import { BaseModal } from '../../../../components/BaseModal';
import { useSsoEmailDomainExclusivity } from '../../hooks';
import { failureMessage } from '../../utils';

import { EmailDomainsTableHeader } from './EmailDomainsTableHeader';

export interface EmailDomainsModalProps {
  isOpen: boolean;
  onClose: () => void;
  connectionId: string;
  /** Every domain with a row on screen, in the state shown now. */
  emailDomains: SsoEmailDomain[];
  /**
   * The domains Cognito no longer routes. Their flag can only be cleared, so
   * switching one back on is refused by the backend anyway.
   */
  staleDomains: Set<string>;
  /** The domains that were written, so the page can show the new state. */
  onSaved: (changes: Record<string, boolean>) => void;
}

const isSameState = (
  draft: Record<string, boolean>,
  emailDomains: SsoEmailDomain[]
) =>
  emailDomains.every(
    (domain) => draft[domain.emailDomain] === domain.exclusiveFederatedLogin
  );

interface DomainSaveOutcome {
  emailDomain: string;
  exclusiveFederatedLogin: boolean;
  /** Set when the backend refused this domain. */
  reason?: string;
}

const toDraft = (emailDomains: SsoEmailDomain[]): Record<string, boolean> =>
  Object.fromEntries(
    emailDomains.map((domain) => [
      domain.emailDomain,
      domain.exclusiveFederatedLogin,
    ])
  );

/**
 * Edits the federated-only flag on the domains Cognito routes to a connection.
 *
 * Nothing is written until Save, and each changed domain is written on its own,
 * because the two directions are not symmetric: turning the flag on is refused
 * for a domain Cognito no longer routes, while turning it off always succeeds.
 * One refusal must not take the other changes down with it, so failures are
 * reported per domain and the ones that landed are kept.
 */
export const EmailDomainsModal: React.FC<EmailDomainsModalProps> = ({
  isOpen,
  onClose,
  connectionId,
  emailDomains,
  staleDomains,
  onSaved,
}) => {
  const { setExclusivity } = useSsoEmailDomainExclusivity();
  const [draft, setDraft] = useState<Record<string, boolean>>(() =>
    toDraft(emailDomains)
  );
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = () => {
    setDraft(toDraft(emailDomains));
    setReasons({});
    onClose();
  };

  const handleSave = async () => {
    setIsSaving(true);
    setReasons({});

    const changed = emailDomains.filter(
      (domain) => draft[domain.emailDomain] !== domain.exclusiveFederatedLogin
    );

    // Written one at a time so a refusal on one domain leaves the rest alone
    // and can be shown against the row it belongs to.
    const outcomes = await changed.reduce<Promise<DomainSaveOutcome[]>>(
      async (previous, domain) => {
        const settled = await previous;
        const exclusive = draft[domain.emailDomain];
        const result = await setExclusivity({
          connectionId,
          emailDomain: domain.emailDomain,
          exclusiveFederatedLogin: exclusive,
        });

        return [
          ...settled,
          {
            emailDomain: domain.emailDomain,
            exclusiveFederatedLogin: exclusive,
            reason:
              result.kind === 'success' ? undefined : failureMessage(result),
          },
        ];
      },
      Promise.resolve([])
    );

    const saved = Object.fromEntries(
      outcomes
        .filter((outcome) => !outcome.reason)
        .map((outcome) => [
          outcome.emailDomain,
          outcome.exclusiveFederatedLogin,
        ])
    );
    const failed: Record<string, string> = Object.fromEntries(
      outcomes.flatMap((outcome) =>
        outcome.reason ? [[outcome.emailDomain, outcome.reason] as const] : []
      )
    );

    setIsSaving(false);
    onSaved(saved);

    if (Object.keys(failed).length === 0) {
      handleClose();
      return;
    }

    // Put the refused rows back where they were, so what is on screen is what
    // is stored, and say why beside each one.
    setDraft((current) => ({
      ...current,
      ...Object.fromEntries(
        emailDomains
          .filter((domain) => failed[domain.emailDomain])
          .map((domain) => [domain.emailDomain, domain.exclusiveFederatedLogin])
      ),
    }));
    setReasons(failed);
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Email domains"
      subtitle="Choose which domains may only sign in through this provider. Switch off any domain this connection no longer uses."
      footerContent={{
        onCancel: handleClose,
        onConfirm: handleSave,
        confirmText: 'Save domains',
        isLoading: isSaving,
        isDisabled: isSameState(draft, emailDomains),
      }}
    >
      <div className="overflow-hidden rounded-lg border border-default-200">
        <EmailDomainsTableHeader />
        <div className="divide-y divide-default-200">
          {emailDomains.map((domain) => (
            <div key={domain.emailDomain} className="px-4 py-3">
              <div className="flex items-center justify-between gap-4">
                <span className="flex items-center gap-2">
                  <span className="font-mono text-sm text-foreground">
                    {domain.emailDomain}
                  </span>
                  {staleDomains.has(domain.emailDomain) && (
                    <Chip size="sm" variant="flat" color="warning">
                      No longer used by this connection
                    </Chip>
                  )}
                </span>
                {staleDomains.has(domain.emailDomain) ? (
                  // A de-routed domain has one useful move: clear the row. A
                  // switch would offer to turn it back on, which the backend
                  // refuses, so this is a plain remove that Save carries out.
                  <Button
                    size="sm"
                    variant="flat"
                    color={draft[domain.emailDomain] ? 'danger' : 'default'}
                    isDisabled={isSaving}
                    onPress={() =>
                      setDraft((current) => ({
                        ...current,
                        [domain.emailDomain]: !current[domain.emailDomain],
                      }))
                    }
                  >
                    {draft[domain.emailDomain] ? 'Remove' : 'Undo remove'}
                  </Button>
                ) : (
                  <Switch
                    size="sm"
                    aria-label={`Exclusive SSO login for ${domain.emailDomain}`}
                    isSelected={draft[domain.emailDomain] ?? false}
                    isDisabled={isSaving}
                    onValueChange={(exclusive) =>
                      setDraft((current) => ({
                        ...current,
                        [domain.emailDomain]: exclusive,
                      }))
                    }
                  />
                )}
              </div>
              {reasons[domain.emailDomain] && (
                <p className="mt-1 text-xs text-danger">
                  {reasons[domain.emailDomain]}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </BaseModal>
  );
};
